import { useState, useEffect } from "react";
import "./AIAnalysis.css";
import apiService from "../services/apiService";
import CaseForm from "./CaseForm";
import CaseSuccess from "./CaseSuccess";

/* global Office */

const INITIAL_FIELDS = {
    subject: "",
    contactId: "",
    policyId: "",
    caseType: "",
    priority: "",
    description: "",
    dateOfLoss: "",
    locationOfIncident: "",
    otherPartyInvolved: false,
    otherPartyInsuranceInfo: "",
    policeReportFiled: "",
    injuriesReported: "",
    faultStatus: "",
};

const INITIAL_SOURCES = {
    subject: "email",
    contactId: "sf",
    policyId: "sf",
    caseType: "ai",
    priority: "ai",
    description: "ai",
    dateOfLoss: "ai",
    locationOfIncident: "ai",
    otherPartyInvolved: "ai",
    otherPartyInsuranceInfo: "ai",
    policeReportFiled: "ai",
    injuriesReported: "ai",
    faultStatus: "ai",
};

const getOutlookAnalysisMetadata = () => {
    try {
        const mailbox = Office.context?.mailbox;
        const item = mailbox?.item;

        return {
            messageId: item?.internetMessageId || item?.itemId || "",
            userEmail: mailbox?.userProfile?.emailAddress || "",
        };
    } catch (error) {
        console.log("Failed to read Outlook analysis metadata", error);
        return { messageId: "", userEmail: "" };
    }
};

// Module-level cache to persist state across tab switches
let cachedAiState = {
    analyzed: false,
    fields: INITIAL_FIELDS,
    sources: INITIAL_SOURCES,
    submitted: false,
    caseResult: null,
    followupDraft: "",
    accountId: null,
    messageId: null,
    attachments: [],
};

const AIAnalysis = ({ sfContext, policies, sfAuthError, sfSession, refreshSfToken }) => {
    const { messageId: currentMessageId } = getOutlookAnalysisMetadata();
    const [loading, setLoading] = useState(false);
    const [analyzed, setAnalyzed] = useState(cachedAiState.analyzed);
    const [fields, setFields] = useState(cachedAiState.fields);
    const [fieldSources, setFieldSources] = useState(cachedAiState.sources);
    const [submitted, setSubmitted] = useState(cachedAiState.submitted);
    const [error, setError] = useState(null);
    const [creatingCase, setCreatingCase] = useState(false);
    const [caseResult, setCaseResult] = useState(cachedAiState.caseResult);
    const [followupDraft, setFollowupDraft] = useState(cachedAiState.followupDraft);
    const [draftLoading, setDraftLoading] = useState(false);
    const [accountId, setAccountId] = useState(cachedAiState.accountId);
    const [attachments, setAttachments] = useState(cachedAiState.attachments);

    useEffect(() => {
        // Reset cached AI output when the selected email or Salesforce account changes.
        if (sfContext?.accountId !== cachedAiState.accountId || currentMessageId !== cachedAiState.messageId) {
            handleStartOver();
            cachedAiState.accountId = sfContext?.accountId;
            cachedAiState.messageId = currentMessageId;
            setAccountId(sfContext?.accountId);
        }
    }, [currentMessageId, sfContext?.accountId, policies]);

    useEffect(() => {
        // If we are already submitted but the draft wasn't finished, try generating again
        if (submitted && !followupDraft && !loading && !creatingCase) {
            generateFollowupDraft();
        }
    }, []);

    // Update the cache whenever state changes
    const updateAnalyzed = (val) => {
        cachedAiState.analyzed = val;
        setAnalyzed(val);
    };

    const updateFields = (fieldUpdater) => {
        setFields((prev) => {
            const next = typeof fieldUpdater === "function" ? fieldUpdater(prev) : fieldUpdater;
            cachedAiState.fields = next;
            return next;
        });
    };

    const updateSources = (key, source) => {
        setFieldSources((prev) => {
            const next = { ...prev, [key]: source };
            cachedAiState.sources = next;
            return next;
        });
    };

    const updateSubmitted = (val) => {
        cachedAiState.submitted = val;
        console.log("checking fields", fields)
        setSubmitted(val);
    };

    const updateFollowupDraft = (val) => {
        cachedAiState.followupDraft = val;
        setFollowupDraft(val);
    };

    const updateAttachments = (val) => {
        cachedAiState.attachments = val;
        setAttachments(val);
    };

    const handleAnalyze = async () => {
        setLoading(true);
        try {
            setError(null);
            
            // Restored real backend call for detailed email analysis
            const metadata = getOutlookAnalysisMetadata();

            if (!metadata.messageId) {
                setError("Unable to identify this Outlook message. Please reopen the email and try again.");
                return;
            }

            // Get email body from Office.js
            let emailBody = "";
            try {
                const item = Office.context?.mailbox?.item;
                if (item) {
                    emailBody = await new Promise((resolve) => {
                        item.body.getAsync("text", (result) => {
                            resolve(result.status === "succeeded" ? result.value : "");
                        });
                    });
                }
            } catch (e) {
                console.log("Office.js not available, using empty body");
            }

            const data = await apiService.analyzeEmailDetailed({
                emailBody,
                messageId: metadata.messageId,
                userEmail: metadata.userEmail,
            });

            /*
            // Mock data bypass (Commented out)
            await new Promise(resolve => setTimeout(resolve, 2000));
            const data = {
                caseType: "Claim",
                priority: "High",
                description: "The customer is reporting a minor accident involving another vehicle. No injuries were reported, but there is damage to the front bumper.",
                dateOfLoss: "2024-05-10",
                locationOfIncident: "123 Main St, Springfield",
                otherPartyInvolved: true,
                otherPartyInsuranceInfo: "State Farm, Policy #987654",
                policeReportFiled: "Yes",
                injuriesReported: "No",
                faultStatus: "Undetermined",
            };
            */

            // Get attachments metadata (Keep this part as it uses Office.js data which is local context)
            let attachmentMetadata = [];
            try {
                const item = Office.context?.mailbox?.item;
                if (item && item.attachments) {
                    attachmentMetadata = item.attachments.map(att => ({
                        id: att.id,
                        name: att.name,
                        contentType: att.contentType,
                        size: att.size,
                        attachmentType: att.attachmentType,
                        selected: true // default select all
                    }));
                }
            } catch (e) {
                console.log("Error fetching attachment metadata", e);
            }
            updateAttachments(attachmentMetadata);

            // Update fields with AI response
            updateFields((prev) => ({
                ...prev,
                caseType: data.caseType || prev.caseType,
                priority: data.priority || prev.priority,
                description: data.description || prev.description,
                dateOfLoss: data.dateOfLoss || prev.dateOfLoss,
                locationOfIncident: data.locationOfIncident || prev.locationOfIncident,
                otherPartyInvolved: data.otherPartyInvolved ?? prev.otherPartyInvolved,
                otherPartyInsuranceInfo: data.otherPartyInsuranceInfo || prev.otherPartyInsuranceInfo,
                policeReportFiled: data.policeReportFiled || prev.policeReportFiled,
                injuriesReported: data.injuriesReported || prev.injuriesReported,
                faultStatus: data.faultStatus || prev.faultStatus,
            }));

            // Set subject, contact, and policy from Office.js and Salesforce Context
            try {
                const item = Office.context?.mailbox?.item;
                if (item) {
                    const firstAccountContact = sfContext.contacts?.[0];
                    const firstPolicy = policies?.[0];

                    updateFields((prev) => ({
                        ...prev,
                        subject: item.subject || prev.subject,
                        // Value will be ID 
                        contactId: firstAccountContact?.contactId || prev.contactId,
                        policyId: firstPolicy?.id || prev.policyId,
                    }));
                }
            } catch (e) { /* ignore */ }

            updateAnalyzed(true);
            // Reset sources to initial on new analysis
            setFieldSources(INITIAL_SOURCES);
            cachedAiState.sources = INITIAL_SOURCES;
        } catch (error) {
            console.error("Analysis failed:", error);
            setError("Backend server not working. Please check if the backend is running.");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCase = async () => {
        setCreatingCase(true);
        setError(null);

        /*
        if (sfAuthError) {
            setError(`Salesforce authentication failed: ${sfAuthError.error || "Access restricted"}. Case creation is disabled.`);
            setCreatingCase(false);
            return;
        }
        */

        try {
            /*
            let token = sfSession?.accessToken;
            let instanceUrl = sfSession?.instanceUrl;

            if (!token || !instanceUrl) {
                // Try refreshing the token
                const refreshed = await refreshSfToken?.();
                if (refreshed) {
                    token = refreshed.accessToken;
                    instanceUrl = refreshed.instanceUrl;
                } else {
                    setError("Missing Salesforce authentication. Please retry.");
                    setCreatingCase(false);
                    return;
                }
            }

            // Build payload
            const payload = {
                subject: fields.subject,
                contactId: fields.contactId,
                policyId: fields.policyId,
                caseType: fields.caseType,
                priority: fields.priority,
                description: fields.description,
                dateOfLoss: fields.dateOfLoss,
                locationOfIncident: fields.locationOfIncident,
                otherPartyInvolved: fields.otherPartyInvolved,
                otherPartyInsuranceInfo: fields.otherPartyInsuranceInfo,
                policeReportFiled: fields.policeReportFiled,
                injuriesReported: fields.injuriesReported,
                faultStatus: fields.faultStatus,
                instance_url: instanceUrl,
                access_token: token,
                attachments: [] // Will populate below
            };

            // Fetch selected attachments content
            const selectedAtts = attachments.filter(a => a.selected);
            if (selectedAtts.length > 0) {
                const attachmentsContent = await Promise.all(selectedAtts.map(async (att) => {
                    try {
                        const content = await new Promise((resolve, reject) => {
                            Office.context.mailbox.item.getAttachmentContentAsync(att.id, (result) => {
                                if (result.status === Office.AsyncResultStatus.Succeeded) {
                                    resolve(result.value.content);
                                } else {
                                    reject(new Error(result.error.message));
                                }
                            });
                        });
                        return {
                            name: att.name,
                            contentType: att.contentType,
                            content: content // This is base64 for file attachments in Outlook
                        };
                    } catch (err) {
                        console.error(`Failed to get content for ${att.name}:`, err);
                        return null;
                    }
                }));
                payload.attachments = attachmentsContent.filter(Boolean);
            }
            console.log("checking payload", payload)
            const response = await apiService.createCase(payload);
            */

            // Simulation of MOCK case creation
            await new Promise(resolve => setTimeout(resolve, 2500));
            const response = {
                caseId: "mock_case_id_abc123",
                caseNumber: "00123456",
                status: "Success"
            };

            if (response.error) {
                setError(`Failed to create case: ${response.details || response.error}`);
            } else {
                setCaseResult(response);
                cachedAiState.caseResult = response;
                updateSubmitted(true);

                // Generate follow-up draft IMMEDIATELY if there are missing fields
                if (dynamicMissing.length > 0) {
                    generateFollowupDraft();
                }
            }
        } catch (err) {
            console.error("Create case failed:", err);
            setError("Failed to create case due to a network error.");
        } finally {
            setCreatingCase(false);
        }
    };

    const generateFollowupDraft = async () => {
        if (cachedAiState.followupDraft) return; // Already generated

        setDraftLoading(true);
        try {
            // Restored real backend call for follow-up draft generation
            const metadata = getOutlookAnalysisMetadata();
            const contactName = sfContext?.contacts?.[0]?.name || "there";
            const response = await apiService.generateFollowup({
                messageId: metadata.messageId,
                userEmail: metadata.userEmail,
                contactName,
                caseType: fields.caseType,
                description: fields.description,
                missingFields: dynamicMissing,
                filledFields: {
                    subject: fields.subject,
                    caseType: fields.caseType,
                    priority: fields.priority,
                    dateOfLoss: fields.dateOfLoss,
                    locationOfIncident: fields.locationOfIncident,
                },
            });
            
            const draft = response.draft || "Could not generate draft.";

            /*
            // Mock data bypass (Commented out)
            await new Promise(resolve => setTimeout(resolve, 1800));
            const contactName = sfContext?.contacts?.[0]?.name || "there";
            const missingText = dynamicMissing.join(", ");
            const draft = `Subject: Additional information needed for Case ${caseResult?.caseNumber || ""}\n\nDear ${contactName},\n\nThank you for reaching out. We have successfully created a case for your ${fields.caseType || "request"}. However, we still need a few details to proceed efficiently: ${missingText}.\n\nPlease let us know at your earliest convenience.\n\nBest regards,\nClaims Team`;
            */

            setFollowupDraft(draft);
            cachedAiState.followupDraft = draft;
        } catch (err) {
            console.error("Follow-up draft failed:", err);
            setFollowupDraft("Failed to generate follow-up email.");
        } finally {
            setDraftLoading(false);
        }
    };

    const handleStartOver = () => {
        updateAnalyzed(false);
        updateSubmitted(false);
        updateFields(INITIAL_FIELDS);
        setFieldSources(INITIAL_SOURCES);
        cachedAiState.sources = INITIAL_SOURCES;
        setCaseResult(null);
        setFollowupDraft("");
        setDraftLoading(false);
        cachedAiState.caseResult = null;
        cachedAiState.followupDraft = "";
        updateAttachments([]);
        // Note: we don't reset cachedAiState.accountId/messageId here
        // because they are used to detect when the selected email changes.
    };

    const handleFieldChange = (key, value) => {
        updateFields((prev) => ({ ...prev, [key]: value }));
        updateSources(key, "user");
    };

    const handleToggleAttachment = (id) => {
        updateAttachments(attachments.map(att =>
            att.id === id ? { ...att, selected: !att.selected } : att
        ));
    };

    // Count all fields dynamically
    const allFieldKeys = Object.keys(INITIAL_FIELDS);
    const totalFields = allFieldKeys.length; // 13

    const filledCount = allFieldKeys.filter((key) => {
        const val = fields[key];
        if (typeof val === "boolean") return true; // booleans are always "filled"
        return val !== "" && val !== null && val !== undefined;
    }).length;

    const dynamicMissing = [
        !fields.subject && "Subject",
        !fields.contactId && "Contact",
        !fields.policyId && "Related Policy",
        !fields.caseType && "Case Type",
        !fields.priority && "Priority",
        !fields.description && "Description",
        !fields.dateOfLoss && "Date of Loss",
        !fields.policeReportFiled && "Police Report Filed",
        !fields.injuriesReported && "Injuries Reported",
        !fields.faultStatus && "Fault Status",
        !fields.locationOfIncident && "Location of Incident",
        !fields.otherPartyInsuranceInfo && "Other Party Insurance Info",
    ].filter(Boolean);

    const missingCount = dynamicMissing.length;

    return (
        <div className="ai-analysis-wrapper">

            {/* ══════════════════════════════════════
                INTRO CARD — only before analysis
                ══════════════════════════════════════ */}
            {/* ══════════════════════════════════════
                MAIN CARD
                ══════════════════════════════════════ */}
            {!submitted && (
                <div className="card">
                    <div className="card-header">
                        <span className="card-header-icon">🤖</span>
                        <h3 className="card-header-title">AI Analysis</h3>
                    </div>

                    <div className="analysis-divider" />

                    {!analyzed ? (
                        <div className="analysis-body">
                            {loading ? (
                                <div className="analysis-loading-container">
                                    <div className="large-spinner" />
                                    <div className="loading-text-main">Analyzing email...</div>
                                    <div className="loading-text-sub">Fetching Case fields from Salesforce</div>
                                </div>
                            ) : (
                                <>
                                    <p className="analysis-intro">
                                        Click below to analyze this email. The AI will:
                                    </p>
                                    <ul className="analysis-list">
                                        <li>Classify the email intent</li>
                                        <li>Pull Case fields from Salesforce dynamically</li>
                                        <li>Auto-fill fields from email + Salesforce data</li>
                                        <li>Create Case with available info</li>
                                        <li>Draft follow-up email for missing fields</li>
                                    </ul>

                                    {error && (
                                        <div className="error-message">
                                            <span className="error-icon">⚠️</span>
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        className="analyze-btn"
                                        onClick={handleAnalyze}
                                    >
                                        <span className="cart-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                <path d="M5 7H19L17.5 15H6.5L5 7Z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
                                                <path d="M5 7L4 3H2" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                                                <circle cx="9" cy="19" r="1.5" fill="white" />
                                                <circle cx="15" cy="19" r="1.5" fill="white" />
                                            </svg>
                                        </span>
                                        Analyze &amp; Prepare Case
                                    </button>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="analysis-body" style={{ padding: "0" }}>
                            <CaseForm
                                fields={fields}
                                handleFieldChange={handleFieldChange}
                                filledCount={filledCount}
                                missingCount={missingCount}
                                dynamicMissing={dynamicMissing}
                                onSubmit={handleCreateCase}
                                creatingCase={creatingCase}
                                sfContext={sfContext}
                                policies={policies}
                                fieldSources={fieldSources}
                                attachments={attachments}
                                onToggleAttachment={handleToggleAttachment}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════
                SUCCESS & DRAFT SECTION
                ══════════════════════════════════════ */}
            {submitted && (
                <CaseSuccess
                    caseResult={caseResult}
                    fields={fields}
                    filledCount={filledCount}
                    totalFields={totalFields}
                    dynamicMissing={dynamicMissing}
                    onStartOver={handleStartOver}
                    sfContext={sfContext}
                    followupDraft={followupDraft}
                    draftLoading={draftLoading}
                    updateFollowupDraft={updateFollowupDraft}
                />
            )}

        </div>
    );
};

export default AIAnalysis;
